import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { user } = useAuth();
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPlanInfo();
    } else {
      setPlanInfo(null);
      setLoading(false);
    }
  }, [user]);

  const fetchPlanInfo = async () => {
    try {
      const res = await api.get('/plan-info');
      setPlanInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch plan info:', err);
      // Default to Free plan if fetching fails
      setPlanInfo({
        plan_name: 'free',
        features: {},
        limits: { max_clients: 5, max_projects: 10, max_invoices_per_month: 20 }
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if a feature is enabled for the current plan
   * @param {string} featureKey - e.g., "accountant_handover", "reports", "multi_user_access"
   * @returns {boolean}
   */
  const hasFeature = (featureKey) => {
    if (!planInfo) return false;
    return planInfo.features[featureKey] === true;
  };

  /**
   * Get the limit for a numeric feature
   * @param {string} limitKey - e.g., "max_clients", "max_projects"
   * @returns {number} - -1 means unlimited
   */
  const getLimit = (limitKey) => {
    if (!planInfo) return 0;
    return planInfo.limits[limitKey] ?? -1;
  };

  /**
   * Check if creating a new entity is allowed
   * @param {string} entityType - "client", "project", "invoice"
   * @param {number} currentCount - current count of entities
   * @returns {boolean}
   */
  const canCreate = (entityType, currentCount) => {
    const limitKey = `max_${entityType}s`;
    const limit = getLimit(limitKey);
    if (limit === -1) return true; // Unlimited
    return currentCount < limit;
  };

  /**
   * Get plan name
   * @returns {string} - "free", "pro", etc.
   */
  const getPlanName = () => {
    return planInfo?.plan_name || 'free';
  };

  /**
   * Check if user is on Free plan
   * @returns {boolean}
   */
  const isFreePlan = () => {
    return getPlanName() === 'free';
  };

  /**
   * Check if user is on Pro plan or higher
   * @returns {boolean}
   */
  const isProPlan = () => {
    const name = getPlanName();
    return name === 'pro' || name === 'business' || name === 'enterprise';
  };

  const isBusinessPlan = () => {
    const name = getPlanName();
    return name === 'business' || name === 'enterprise';
  };

  const getStatus = () => {
    return planInfo?.status || 'active';
  };

  const isActive = () => {
    return getStatus() === 'active' || getStatus() === 'trialing';
  };

  return (
    <PlanContext.Provider
      value={{
        planInfo,
        loading,
        hasFeature,
        getLimit,
        canCreate,
        getPlanName,
        isFreePlan,
        isProPlan,
        isBusinessPlan,
        getStatus,
        isActive,
        refresh: fetchPlanInfo,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used within PlanProvider');
  return ctx;
}
